import { ValidateUpdateResponse } from '@jucy-ui/common';
import { BookingOptions } from '@jucy-ui/common/services';
import { SelfServiceBooking } from '@jucy/rentals-api-client/rentals-api-v3';
import { TripDetails } from '@jucy/rentals-api-client/rentals-api-v3/models/TripDetails';
import localForage from 'localforage';
import { IObservableArray, makeAutoObservable, observable, runInAction, toJS } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { SetRequired } from 'type-fest';

class AmendmentStore {
    amendments: IObservableArray<ValidateUpdateResponse> = observable.array([]);

    constructor(initialData?: Partial<AmendmentStore>) {
        makeAutoObservable(this);
        Object.assign(this, initialData);
        if (typeof window !== 'undefined') {
            makePersistable(this, {
                name: 'AmendmentStore',
                properties: ['amendments'],
                storage: localForage,
                expireIn: 86400000,
                removeOnExpiration: true,
                stringify: false,
            });
        }
    }

    hydrate = (data: Partial<AmendmentStore>): void => {
        Object.assign(this, data);
    };

    addAmendment(update: ValidateUpdateResponse) {
        const exists = this.amendments.find((amendment) => amendment.reference === update.reference);
        runInAction(() => {
            if (exists) {
                this.amendments.remove(exists);
            }
            this.amendments.push(update);
        });
    }

    getAmendmentByReservationRef(reservationRef: string) {
        return this.amendments.find((amendment) => amendment.reference === reservationRef);
    }

    findOrCreateAmendment({
        booking,
        bookingOptions,
    }: {
        booking: SelfServiceBooking;
        bookingOptions: BookingOptions;
    }): SetRequired<ValidateUpdateResponse, 'updates' | 'original'> {
        const bookingJSON = toJS(booking);
        const bookingOptionsJSON = toJS(bookingOptions);
        const bookingAsUpdates: TripDetails = structuredClone({
            pickUpLocation: bookingJSON.pickUpLocation,
            dropOffLocation: bookingJSON.dropOffLocation,
            pickUpDate: bookingJSON.pickUpDate,
            dropOffDate: bookingJSON.dropOffDate,
            rentalDays: bookingJSON.rentalDays,
            totalPrice: bookingJSON.totalPrice,
            lines: bookingJSON.lines,
            fleetCategory: bookingJSON.fleetCategory,
            secondaryProducts: bookingOptionsJSON.secondaryProducts,
            insuranceProducts: bookingOptionsJSON.insuranceProducts,
            bundles: bookingOptionsJSON.bundles,
        });

        const existing = toJS(this.getAmendmentByReservationRef(booking.reservationReference));
        if (existing) {
            return {
                ...existing,
                updates: existing.updates || bookingAsUpdates,
                original: existing.original || bookingJSON,
            };
        }

        return {
            updates: bookingAsUpdates,
            original: bookingJSON,
            status: 'edit',
            reference: bookingJSON.reservationReference,
            bookingNo: bookingJSON.reservationId,
        };
    }

    removeAmendment(amendmentOrReservationRef: ValidateUpdateResponse | string) {
        const amendment =
            typeof amendmentOrReservationRef === 'string' ? this.getAmendmentByReservationRef(amendmentOrReservationRef) : amendmentOrReservationRef;
        if (!amendment) {
            return;
        }
        runInAction(() => {
            this.amendments.remove(amendment);
        });
    }
}

export const amendmentStore = new AmendmentStore();
